import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../../context/AuthContext';
import axios from 'axios';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    Card,
    CardBody,
    Flex,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Badge,
    useBreakpointValue,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    Input,
    InputGroup,
    InputLeftElement,
    useToast,
    IconButton,
    Collapse,
} from '@chakra-ui/react';
import {
    FaBook,
    FaUsers,
    FaSync,
    FaUser,
    FaMapMarkerAlt,
    FaCalendar,
    FaSearch,
    FaChartLine,
    FaShieldAlt,
} from 'react-icons/fa';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import EmptyStateCard from '../../compnents/EmptyStateCard';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardRMownBookPaid() {
    const { token } = useContext(AuthContext);
    const [rmData, setRmData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const toast = useToast();

    // Responsive breakpoint
    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    const fetchRMownBook = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/content/rm-own-book`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setRmData(response.data);
        } catch (err) {
            console.error('Error fetching RM own book:', err);
            const errorMsg = err.response?.data?.message || 'Failed to fetch customer book';
            setError(errorMsg);
            toast({
                title: 'Error',
                description: errorMsg,
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchRMownBook();
        }
        // eslint-disable-next-line
    }, [token]);

    const getRiskColor = (riskProfile) => {
        if (riskProfile <= 3) return 'green';
        if (riskProfile <= 6) return 'yellow';
        if (riskProfile <= 8) return 'orange';
        return 'red';
    };

    const getRiskLabel = (riskProfile) => {
        if (riskProfile <= 3) return 'Low';
        if (riskProfile <= 6) return 'Medium';
        if (riskProfile <= 8) return 'High';
        return 'Very High';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const customers = rmData?.customers || [];
    const customerCount = rmData?.customer_count || 0;
    const rmName = rmData?.rm_name || 'RM';

    // Filter customers based on search
    const filteredCustomers = customers.filter(customer =>
        customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.country_iso.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Calculate statistics
    const avgRisk = customers.length > 0
        ? (customers.reduce((sum, c) => sum + c.risk_profile, 0) / customers.length).toFixed(1)
        : 0;

    const highRiskCount = customers.filter(c => c.risk_profile >= 7).length;

    // Toggle customer details
    const toggleCustomer = (customerId) => {
        if (expandedCustomer === customerId) {
            setExpandedCustomer(null);
        } else {
            setExpandedCustomer(customerId);
        }
    };

    // Handle search
    const handleSearch = () => {
        // Search is handled by filteredCustomers already
    };

    return (
        <Box
            minH="100vh"
            width="100%"
            pb={{ base: "90px", md: "20px" }} // Extra padding on mobile for bottom navbar
        >
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    {/* Header */}
                    <Box textAlign="center" pt={{ base: 2, md: 4 }}>
                        <Heading
                            size={{ base: 'lg', md: 'xl' }}
                            bgGradient="linear(to-r, brand.navy, brand.tealDark)"
                            bgClip="text"
                            mb={2}
                            fontWeight="extrabold"
                        >
                            <HStack justify="center" spacing={2}>
                                <Icon as={FaBook} boxSize={{ base: 6, md: 8 }} />
                                <Text>My Customer Book</Text>
                            </HStack>
                        </Heading>
                        <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }} px={{ base: 4, md: 0 }}>
                            View and manage your assigned customer portfolio
                        </Text>
                        {rmData && (
                            <HStack justify="center" mt={3} spacing={2}>
                                <Icon as={FaUser} color="brand.tealDark" boxSize={{ base: 4, md: 5 }} />
                                <Text color="brand.navy" fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }}>
                                    {rmName}
                                </Text>
                            </HStack>
                        )}
                    </Box>

                    {/* Statistics Cards */}
                    {!loading && rmData && (
                        <SimpleGrid columns={{ base: 3, md: 3 }} spacing={{ base: 3, md: 4 }}>
                            <Card
                                bg="white"
                                borderRadius="xl"
                                boxShadow="sm"
                                border="1px"
                                borderColor="gray.100"
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaUsers} color="brand.tealDark" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                Total Customers
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber
                                            fontSize={{ base: '2xl', md: '3xl' }}
                                            color="brand.navy"
                                            fontWeight="bold"
                                        >
                                            {customerCount}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card
                                bg="white"
                                borderRadius="xl"
                                boxShadow="sm"
                                border="1px"
                                borderColor="gray.100"
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaChartLine} color="brand.teal" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                Avg Risk
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber
                                            fontSize={{ base: '2xl', md: '3xl' }}
                                            color="brand.navy"
                                            fontWeight="bold"
                                        >
                                            {avgRisk}/10
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card
                                bg="white"
                                borderRadius="xl"
                                boxShadow="sm"
                                border="1px"
                                borderColor="gray.100"
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaShieldAlt} color="orange.500" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                                                High Risk
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber
                                            fontSize={{ base: '2xl', md: '3xl' }}
                                            color="orange.600"
                                            fontWeight="bold"
                                        >
                                            {highRiskCount}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>
                        </SimpleGrid>
                    )}

                    {/* Error Alert */}
                    {error && (
                        <Alert
                            status="error"
                            borderRadius="xl"
                            boxShadow="sm"
                            variant="left-accent"
                        >
                            <AlertIcon />
                            <Box flex="1">
                                <AlertTitle fontSize={{ base: 'sm', md: 'md' }}>Error!</AlertTitle>
                                <AlertDescription fontSize={{ base: 'xs', md: 'sm' }}>{error}</AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Search and Refresh */}
                    <Card bg="white" borderRadius="lg" boxShadow="md">
                        <CardBody>
                            <HStack spacing={4}>
                                <InputGroup flex={1}>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaSearch} color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by Customer ID or Country..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        borderColor="gray.300"
                                        _focus={{ borderColor: 'brand.teal', boxShadow: '0 0 0 1px var(--chakra-colors-brand-teal)' }}
                                    />
                                </InputGroup>
                                <Button
                                    leftIcon={<FaSync />}
                                    colorScheme="teal"
                                    onClick={fetchRMownBook}
                                    isLoading={loading}
                                    _hover={{ bg: 'brand.navy' }}
                                >
                                    Refresh
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Customer List */}
                    {loading ? (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color="brand.tealDark" thickness="4px" />
                                <Text color="gray.500" fontSize="sm">Loading customers...</Text>
                            </VStack>
                        </Flex>
                    ) : filteredCustomers.length === 0 ? (
                        <Card bg="white" borderRadius="lg" boxShadow="md">
                            <CardBody>
                                <EmptyStateCard
                                    icon={searchTerm ? FaSearch : FaBook}
                                    title={searchTerm ? 'No customers match this search' : 'Your customer book is empty for now'}
                                    description={
                                        searchTerm
                                            ? `We could not find any customers matching "${searchTerm}".`
                                            : 'Assigned customer relationships will appear here as soon as the workspace has uploaded and mapped them to your RM account.'
                                    }
                                    hint={
                                        searchTerm
                                            ? 'Try a broader customer ID or country search, or clear the search to return to the full list.'
                                            : 'If assignments were updated recently, refresh this page after processing completes.'
                                    }
                                    borderColor="brand.divider"
                                    titleColor="gray.700"
                                    descriptionColor="gray.600"
                                    hintColor="gray.500"
                                    iconColor="brand.tealDark"
                                    maxW="full"
                                >
                                    {searchTerm ? (
                                        <Button
                                            variant="outline"
                                            borderColor="brand.teal"
                                            color="brand.tealDark"
                                            _hover={{ bg: 'brand.tealSoft' }}
                                            onClick={() => setSearchTerm('')}
                                        >
                                            Clear search
                                        </Button>
                                    ) : (
                                        <Button colorScheme="teal" onClick={fetchRMownBook}>
                                            Refresh book
                                        </Button>
                                    )}
                                </EmptyStateCard>
                            </CardBody>
                        </Card>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {filteredCustomers.map((customer, index) => (
                                <Card
                                    key={customer.customer_id || index}
                                    bg="white"
                                    borderRadius="lg"
                                    boxShadow="md"
                                    transition="all 0.2s"
                                    _hover={{ boxShadow: 'lg' }}
                                >
                                    <CardBody>
                                        <Flex justify="space-between" align="center">
                                            <VStack align="start" spacing={1} flex={1}>
                                                <Heading size="sm" color="brand.navy">
                                                    {customer.customer_id}
                                                </Heading>
                                                <HStack spacing={4} flexWrap="wrap">
                                                    <HStack spacing={1}>
                                                        <Icon as={FaMapMarkerAlt} color="gray.400" boxSize={3.5} />
                                                        <Text fontSize="sm" color="gray.600">
                                                            {customer.country_iso}
                                                        </Text>
                                                    </HStack>
                                                    <HStack spacing={1}>
                                                        <Icon as={FaUser} color="gray.400" boxSize={3.5} />
                                                        <Text fontSize="sm" color="gray.600">
                                                            {customer.age} years
                                                        </Text>
                                                    </HStack>
                                                    <Badge
                                                        colorScheme={getRiskColor(customer.risk_profile)}
                                                        px={3}
                                                        py={1}
                                                        borderRadius="full"
                                                        fontSize="xs"
                                                        fontWeight="bold"
                                                    >
                                                        {getRiskLabel(customer.risk_profile)} ({customer.risk_profile}/10)
                                                    </Badge>
                                                </HStack>
                                            </VStack>
                                            <IconButton
                                                icon={
                                                    expandedCustomer === customer.customer_id ? (
                                                        <ChevronUpIcon />
                                                    ) : (
                                                        <ChevronDownIcon />
                                                    )
                                                }
                                                onClick={() => toggleCustomer(customer.customer_id)}
                                                colorScheme="teal"
                                                variant="ghost"
                                                aria-label="Toggle details"
                                            />
                                        </Flex>

                                        <Collapse in={expandedCustomer === customer.customer_id} animateOpacity>
                                            <Box mt={6} pt={6} borderTop="1px solid" borderColor="gray.200">
                                                <VStack align="stretch" spacing={4}>
                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                        <Card bg="brand.tealSoft" borderRadius="md" boxShadow="sm">
                                                            <CardBody>
                                                                <HStack spacing={2} mb={2}>
                                                                    <Icon as={FaUser} color="brand.tealDark" boxSize={5} />
                                                                    <Text fontWeight="bold" color="gray.700">Customer Details</Text>
                                                                </HStack>
                                                                <VStack align="stretch" spacing={2}>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Customer ID:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                            {customer.customer_id}
                                                                        </Text>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Age:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                            {customer.age} years
                                                                        </Text>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Country:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                            {customer.country_iso}
                                                                        </Text>
                                                                    </HStack>
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>

                                                        <Card bg="brand.tealSoft" borderRadius="md" boxShadow="sm">
                                                            <CardBody>
                                                                <HStack spacing={2} mb={2}>
                                                                    <Icon as={FaShieldAlt} color="brand.tealDark" boxSize={5} />
                                                                    <Text fontWeight="bold" color="gray.700">Risk Information</Text>
                                                                </HStack>
                                                                <VStack align="stretch" spacing={2}>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Risk Profile:</Text>
                                                                        <Badge
                                                                            colorScheme={getRiskColor(customer.risk_profile)}
                                                                            px={3}
                                                                            py={1}
                                                                            borderRadius="full"
                                                                            fontSize="xs"
                                                                            fontWeight="bold"
                                                                        >
                                                                            {customer.risk_profile}/10
                                                                        </Badge>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Risk Level:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                            {getRiskLabel(customer.risk_profile)}
                                                                        </Text>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color="gray.600">Member Since:</Text>
                                                                        <HStack spacing={1}>
                                                                            <Icon as={FaCalendar} color="gray.400" boxSize={3} />
                                                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                                {formatDate(customer.created_at)}
                                                                            </Text>
                                                                        </HStack>
                                                                    </HStack>
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>
                                                    </SimpleGrid>
                                                </VStack>
                                            </Box>
                                        </Collapse>
                                    </CardBody>
                                </Card>
                            ))}
                        </VStack>
                    )}

                    {/* Summary footer */}
                    {!loading && filteredCustomers.length > 0 && (
                        <Card bg="white" borderRadius="lg" boxShadow="md">
                            <CardBody>
                                <Text fontSize="sm" color="gray.600" textAlign="center">
                                    Showing {filteredCustomers.length} of {customerCount} customers
                                </Text>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardRMownBookPaid;
